import { useAlert } from 'context/alertContext';
import { useState } from 'react';

interface FormState {
  [key: string]: string;
}

// Create a type for the required fields configuration
type RequiredFields = string[] | 'all' | ((values: FormState) => boolean);

const useForm = (
  initialState: FormState, 
  onSubmit: () => void, 
  requiredFields: RequiredFields = 'all' // Default to validating all fields for backward compatibility
) => {
  const [values, setValues] = useState<FormState>(initialState);
  const { setErrorMessage } = useAlert();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value,
    });
  };

  const resetForm = () => {
    setValues(initialState);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    // Validation logic based on the requiredFields parameter
    let isValid = true;
    
    if (typeof requiredFields === 'function') {
      // Use the custom validation function if provided
      isValid = requiredFields(values);
      if (!isValid) {
        setErrorMessage('Please fill all required fields');
      }
    } else if (requiredFields === 'all') {
      // Original behavior - check all fields
      const isEmpty = Object.values(values).some(
        (value) => value?.toString().trim() === '',
      );
      if (isEmpty) {
        setErrorMessage('Please fill all fields');
        isValid = false;
      }
    } else if (Array.isArray(requiredFields)) {
      // Check only specified required fields
      const missingRequiredFields = requiredFields.filter(
        (field) => !values[field] || values[field].toString().trim() === ''
      );
      
      if (missingRequiredFields.length > 0) {
        setErrorMessage('Please fill all required fields');
        isValid = false;
      }
    }

    // Email validation for forms with email field
    if (isValid && values.email) {
      if (!validateEmail(values.email)) {
        setErrorMessage('Please enter a valid email address');
        isValid = false;
      }
    }

    if (isValid) {
      onSubmit();
    }
  };

  return { values, handleChange, resetForm, handleSubmit };
};

export default useForm;